import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/payments/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
        } else if (response.data.status === 'expired') {
          setStatus('error');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center animate-fadeIn" data-testid="payment-result">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-400 mx-auto animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6">
              Verifying Payment
            </h1>
            <p className="text-zinc-500 mt-2">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6">
              Payment Successful!
            </h1>
            <p className="text-zinc-500 mt-2">
              Your tokens have been added to your account.
            </p>
            <Button
              onClick={() => navigate('/')}
              data-testid="go-to-generator-btn"
              className="mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            >
              Start Creating Cards
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-400 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6">
              Payment Processing
            </h1>
            <p className="text-zinc-500 mt-2">
              Your payment is still being processed. Tokens will be added shortly.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="mt-6 h-12 bg-white/10 hover:bg-white/20 text-white"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6">
              Payment Failed
            </h1>
            <p className="text-zinc-500 mt-2">
              Something went wrong with your payment. Please try again.
            </p>
            <Button
              onClick={() => navigate('/deposit')}
              data-testid="try-again-btn"
              className="mt-6 h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            >
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
